/**
 * Host-page styling for the injected Salesforce embedded messaging widget.
 *
 * The dashboard AppBar is fixed at zIndex appBar + 1 (1101). The SDK injects the widget at
 * document.body, so without an override its header (menu / minimize / close) can sit under that
 * AppBar. Raising the container and frame together avoids a parent stacking context clamping the
 * child.
 *
 * Salesforce doesn't support custom CSS against the widget's internal class names, so the
 * selectors below must be re-verified after each Salesforce release.
 */
export const STYLE_TAG_ID = 'salesforce-embedded-styles-global';

/**
 * Below 640px the SDK's own stylesheet (`<siteUrl>/assets/styles/init.min.css`) turns the maximized
 * frame into a full-screen sheet (`bottom/left/right: 0; width: 100%; height: 100dvh`). Docking it
 * to the bottom at a fraction of that height keeps the page visible behind the chat, so guests can
 * tell the chat is an overlay they need to minimize to get back to the page.
 *
 * The breakpoint is px to line up exactly with the SDK's `max-width: 639px`: between 640px and the
 * SDK's 48em desktop breakpoint the frame is a fixed 480px-wide window that must keep its own
 * placement.
 */
const MOBILE_SHEET_BREAKPOINT = '639px';
const MOBILE_SHEET_HEIGHT = '67';

/** Class the SDK puts on `body` to lock page scrolling, and the range its own rules cover. */
const SDK_SCROLL_LOCK_CLASS = 'embeddedMessagingPreventScrolling';
const MOBILE_SCROLL_LOCK_BREAKPOINT = '47.9375em';

/**
 * z-index 1250 sits above MUI's appBar (1100) and drawer (1200) but below modal (1300), so app
 * dialogs, popovers, and snackbars still layer over the widget.
 *
 * The state class the SDK puts on the frame is `maximized` (`initial` / `minimized` / `inline` are
 * the others), so the mobile rules leave the minimized launch button and its unread notification on
 * the SDK's own placement. Selectors mirror the SDK's own child-combinator form so `!important`
 * decides the tie. `dvh` follows the shrinking viewport when mobile browser chrome shows; the
 * percentage above it is the fallback for browsers without it.
 *
 * The container turns into a full-viewport fixed overlay while maximized, so it has to stop
 * swallowing taps or the page uncovered above the sheet would look interactive but be dead. The
 * frame opts back in; the SDK's minimized notification sets its own `pointer-events`.
 *
 * The SDK also pins `body` while maximized on mobile user agents, which scrolls the page behind the
 * sheet to the top. Keeping the page where the guest left it matters now that it's visible, so the
 * lock is reduced to `overflow: hidden`.
 */
const EMBEDDED_MESSAGING_CSS = `
.embedded-messaging,
.embeddedMessagingFrame {
  z-index: 1250 !important;
}

@media only screen and (max-width: ${MOBILE_SHEET_BREAKPOINT}) {
  .embedded-messaging:has(> .embeddedMessagingFrame[class~="maximized"]) {
    pointer-events: none !important;
  }

  .embedded-messaging > .embeddedMessagingFrame[class~="maximized"] {
    pointer-events: auto !important;
    top: auto !important;
    bottom: 0 !important;
    height: ${MOBILE_SHEET_HEIGHT}% !important;
    height: ${MOBILE_SHEET_HEIGHT}dvh !important;
    max-height: ${MOBILE_SHEET_HEIGHT}% !important;
    max-height: ${MOBILE_SHEET_HEIGHT}dvh !important;
    border-radius: 12px 12px 0 0 !important;
    box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.24) !important;
    overflow: hidden !important;
  }
}

@media only screen and (max-width: ${MOBILE_SCROLL_LOCK_BREAKPOINT}) {
  body.${SDK_SCROLL_LOCK_CLASS} {
    position: static !important;
    width: auto !important;
    height: auto !important;
    cursor: auto !important;
    overflow: hidden !important;
  }
}
`.trim();

/** Adds the stylesheet if it isn't already present. Safe to call repeatedly. */
export function injectEmbeddedMessagingStyles(): void {
  if (typeof document === 'undefined' || document.getElementById(STYLE_TAG_ID)) {
    return;
  }

  const styleEl = document.createElement('style');
  styleEl.id = STYLE_TAG_ID;
  styleEl.textContent = EMBEDDED_MESSAGING_CSS;
  document.head.appendChild(styleEl);
}

/**
 * Removes the stylesheet. Safe to call when nothing is present. The SDK's scroll-lock class goes
 * with it: a logout while the chat is maximized leaves it on `body`, and it locks page scrolling for
 * as long as the SDK's own stylesheet is still loaded.
 */
export function removeEmbeddedMessagingStyles(): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.getElementById(STYLE_TAG_ID)?.remove();
  document.body.classList.remove(SDK_SCROLL_LOCK_CLASS);
}
